import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SignOut, Buildings, ChatCircleText, CheckCircle, Trash, Clock, Star, House, PencilSimple, Plus, WhatsappLogo, EnvelopeSimple, Package, UploadSimple, Image as ImageIcon, FileArrowDown, CurrencyDollar, ChartLineUp, Tag, Warning, Receipt, Key } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RTooltip, CartesianGrid } from "recharts";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BUSINESS } from "@/config";
import { toast } from "sonner";
import { ImageCropper } from "@/components/dashboard/ImageCropper";

const CATEGORIES = ["Performance", "Touring", "All-Season", "SUV", "Off-Road", "Winter"];
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Dashboard() {
  const { user, logout, authHeader } = useAuth();
  const [apps, setApps] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [tires, setTires] = useState([]);
  const [tireDialog, setTireDialog] = useState({ open: false, tire: null });
  const [threshold, setThreshold] = useState(5);
  const [importing, setImporting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [sales, setSales] = useState([]);
  const [brands, setBrands] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/wholesale", { headers: authHeader }).then((r) => setApps(r.data)).catch(() => {});
    api.get("/admin/reviews", { headers: authHeader }).then((r) => setReviews(r.data)).catch(() => {});
    api.get("/tires").then((r) => setTires(r.data)).catch(() => {});
    api.get("/admin/settings", { headers: authHeader }).then((r) => setThreshold(r.data.low_stock_threshold)).catch(() => {});
    api.get("/admin/overview", { headers: authHeader }).then((r) => setOverview(r.data)).catch(() => {});
    api.get("/admin/sales", { headers: authHeader }).then((r) => setSales(r.data)).catch(() => {});
    api.get("/admin/brands", { headers: authHeader }).then((r) => setBrands(r.data)).catch(() => {});
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const pendingReviews = reviews.filter((r) => !r.approved);
  const pendingApps = apps.filter((a) => a.status === "pending");

  const approve = async (id) => {
    await api.patch(`/admin/reviews/${id}/approve`, {}, { headers: authHeader });
    toast.success("Review approved");
    load();
  };
  const removeReview = async (id) => {
    await api.delete(`/admin/reviews/${id}`, { headers: authHeader });
    toast.success("Review deleted");
    load();
  };
  const setStatus = async (id, status) => {
    await api.patch(`/admin/wholesale/${id}/status?status=${status}`, {}, { headers: authHeader });
    toast.success(`Marked ${status}`);
    load();
  };

  const saveTire = async (form, id) => {
    const payload = {
      brand: form.brand, model: form.model,
      width: Number(form.width), aspect: Number(form.aspect), diameter: Number(form.diameter),
      category: form.category, price: Number(form.price), stock: Number(form.stock),
      rating: Number(form.rating) || 4.5, image: form.image || undefined,
    };
    if (id) {
      await api.put(`/admin/tires/${id}`, payload, { headers: authHeader });
      toast.success("Tire updated");
    } else {
      await api.post(`/admin/tires`, payload, { headers: authHeader });
      toast.success("Tire added");
    }
    setTireDialog({ open: false, tire: null });
    load();
  };

  const deleteTire = async (id) => {
    await api.delete(`/admin/tires/${id}`, { headers: authHeader });
    toast.success("Tire deleted");
    load();
  };

  const waDigits = (phone) => (phone || "").replace(/\D/g, "");

  const saveThreshold = async () => {
    await api.put(`/admin/settings?low_stock_threshold=${Number(threshold)}`, {}, { headers: authHeader });
    toast.success(`Low-stock alert set at ${threshold}`);
  };

  const sendDigest = async () => {
    try {
      const { data } = await api.post("/admin/restock-digest/send", {}, { headers: authHeader });
      toast.success(`Restock digest emailed (${data.low_stock_count} low)`);
    } catch {
      toast.error("Could not send digest");
    }
  };

  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const changePw = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password }, { headers: authHeader });
      toast.success("Password updated — use it next time you log in");
      setPw({ current_password: "", new_password: "", confirm: "" });
      setPwOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update password");
    }
    setPwSaving(false);
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/tires/import", fd, { headers: { ...authHeader } });
      toast.success(`Import done: ${data.added} added, ${data.updated} updated${data.errors.length ? `, ${data.errors.length} skipped` : ""}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    }
    setImporting(false);
  };

  const downloadTemplate = () => {
    const csv = "brand,model,width,aspect,diameter,category,price,stock,rating,image\n" +
      "Michelin,Pilot Sport 4S,245,40,18,Performance,289.99,20,4.9,\n" +
      "Bridgestone,Turanza QuietTrack,225,45,17,Touring,179.50,40,4.7,\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tire-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Template downloaded");
  };

  const createSale = async (payload) => {
    await api.post("/admin/sales", payload, { headers: authHeader });
    toast.success("Sale logged");
    load();
  };
  const deleteSale = async (id) => {
    await api.delete(`/admin/sales/${id}`, { headers: authHeader });
    toast.success("Sale removed");
    load();
  };
  const addBrand = async (name, logo) => {
    await api.post("/admin/brands", { name, logo }, { headers: authHeader });
    toast.success("Brand added");
    load();
  };
  const renameBrand = async (id, name, logo) => {
    await api.put(`/admin/brands/${id}`, { name, logo }, { headers: authHeader });
    toast.success("Brand updated");
    load();
  };
  const deleteBrand = async (id) => {
    await api.delete(`/admin/brands/${id}`, { headers: authHeader });
    toast.success("Brand deleted");
    load();
  };

  const stats = [
    { label: "Total revenue", value: money(overview?.total_revenue), icon: CurrencyDollar },
    { label: "Revenue this month", value: money(overview?.revenue_month), icon: ChartLineUp },
    { label: "Inventory value", value: money(overview?.inventory_value), icon: Package },
    { label: "Units sold", value: overview?.units_sold ?? 0, icon: Receipt },
  ];

  return (
    <div className="min-h-screen bg-background grain">
      <header className="border-b border-border bg-black/60 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 bg-primary rounded-full" />
            <span className="font-display font-black text-lg tracking-tighter uppercase">{BUSINESS.name}</span>
            <span className="text-xs text-muted-foreground ml-2 hidden sm:inline">Creator Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="dash-home"><House size={16} /> Site</Link>
            <button onClick={() => setPwOpen(true)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary" data-testid="dash-change-password"><Key size={16} /> Password</button>
            <button onClick={logout} className="flex items-center gap-1.5 text-sm font-bold text-foreground hover:text-primary" data-testid="dash-logout"><SignOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 relative z-10">
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter">Hello, {user?.name || "Creator"}</h1>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {stats.map((s) => (
            <div key={s.label} className="border border-border bg-card p-5" data-testid={`stat-${s.label.replace(/\s+/g, "-")}`}>
              <s.icon size={22} weight="duotone" className="text-primary" />
              <div className="font-display font-black text-4xl mt-3">{s.value}</div>
              <div className="text-xs text-muted-foreground tracking-wide mt-1 uppercase">{s.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="overview" className="mt-10">
          <TabsList className="bg-card border border-border rounded-sm flex-wrap h-auto justify-start">
            <TabsTrigger value="overview" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-sm uppercase text-xs font-bold tracking-wide" data-testid="tab-overview">
              Overview
            </TabsTrigger>
            <TabsTrigger value="sales" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-sm uppercase text-xs font-bold tracking-wide" data-testid="tab-sales">
              Sales ({sales.length})
            </TabsTrigger>
            <TabsTrigger value="inventory" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-sm uppercase text-xs font-bold tracking-wide" data-testid="tab-inventory">
              Inventory ({tires.length})
            </TabsTrigger>
            <TabsTrigger value="brands" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-sm uppercase text-xs font-bold tracking-wide" data-testid="tab-brands">
              Brands ({brands.length})
            </TabsTrigger>
            <TabsTrigger value="applications" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-sm uppercase text-xs font-bold tracking-wide" data-testid="tab-applications">
              Applications ({apps.length})
            </TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-sm uppercase text-xs font-bold tracking-wide" data-testid="tab-reviews">
              Reviews ({pendingReviews.length} pending)
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <OverviewPanel overview={overview} authHeader={authHeader} />
          </TabsContent>

          <TabsContent value="sales" className="mt-6">
            <SalesPanel sales={sales} tires={tires} onCreate={createSale} onDelete={deleteSale} />
          </TabsContent>

          <TabsContent value="brands" className="mt-6">
            <BrandsPanel brands={brands} authHeader={authHeader} onAdd={addBrand} onRename={renameBrand} onDelete={deleteBrand} />
          </TabsContent>

          <TabsContent value="applications" className="mt-6">
            <div className="grid gap-3" data-testid="applications-list">
              {apps.length === 0 && <Empty label="No applications yet" />}
              {apps.map((a, i) => (
                <motion.div key={a.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} className="border border-border bg-card p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between" data-testid="application-row">
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 flex-1">
                    <div><span className="text-xs text-muted-foreground uppercase tracking-wide">Business</span><div className="font-display font-bold text-xl">{a.business_name}</div></div>
                    <div><span className="text-xs text-muted-foreground uppercase tracking-wide">Contact</span><div className="text-sm">{a.contact_name}</div></div>
                    <div><span className="text-xs text-muted-foreground uppercase tracking-wide">Email / Phone</span><div className="text-sm">{a.email} · {a.phone}</div></div>
                    <div><span className="text-xs text-muted-foreground uppercase tracking-wide">Volume</span><div className="text-sm">{a.monthly_volume}</div></div>
                    {a.message && <div className="sm:col-span-2"><span className="text-xs text-muted-foreground uppercase tracking-wide">Message</span><div className="text-sm text-foreground/80">{a.message}</div></div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <a href={`https://wa.me/${waDigits(a.phone)}?text=${encodeURIComponent(`Hi ${a.contact_name}, thanks for applying to ${BUSINESS.name} wholesale.`)}`} target="_blank" rel="noopener noreferrer" title="WhatsApp applicant" className="p-2.5 border border-border rounded-sm hover:border-[#25D366] transition-colors" data-testid={`app-wa-${a.id}`}>
                      <WhatsappLogo size={18} weight="fill" className="text-[#25D366]" />
                    </a>
                    <a href={`mailto:${a.email}?subject=${encodeURIComponent(`${BUSINESS.name} — Wholesale Application`)}&body=${encodeURIComponent(`Hi ${a.contact_name},\n\nThanks for applying for a wholesale account with ${BUSINESS.name}.`)}`} title="Email applicant" className="p-2.5 border border-border rounded-sm hover:border-primary transition-colors" data-testid={`app-email-${a.id}`}>
                      <EnvelopeSimple size={18} weight="fill" className="text-primary" />
                    </a>
                    <StatusBadge status={a.status} />
                    <select value={a.status} onChange={(e) => setStatus(a.id, e.target.value)} className="bg-background border border-border rounded-sm text-sm px-2 py-2 focus:ring-1 focus:ring-primary" data-testid={`app-status-${a.id}`}>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="reviews-list">
              {reviews.length === 0 && <Empty label="No reviews yet" />}
              {reviews.map((r) => (
                <div key={r.id} className="border border-border bg-card p-5 flex flex-col" data-testid="review-row">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-0.5">
                      {[...Array(5)].map((_, s) => <Star key={s} size={13} weight="fill" className={s < r.rating ? "text-primary" : "text-muted"} />)}
                    </div>
                    {r.approved ? <span className="text-[10px] font-bold uppercase text-primary">Live</span> : <span className="text-[10px] font-bold uppercase text-yellow-500">Pending</span>}
                  </div>
                  <h3 className="font-display font-bold text-lg mt-2">{r.title}</h3>
                  <p className="text-sm text-foreground/70 mt-1 flex-1">{r.body}</p>
                  <div className="text-xs text-muted-foreground mt-3">{r.name}{r.vehicle ? ` · ${r.vehicle}` : ""}</div>
                  <div className="flex gap-2 mt-4">
                    {!r.approved && (
                      <button onClick={() => approve(r.id)} className="flex-1 flex items-center justify-center gap-1.5 bg-primary text-white font-bold text-xs uppercase py-2.5 rounded-sm hover:bg-red-500" data-testid={`approve-${r.id}`}>
                        <CheckCircle size={16} weight="bold" /> Approve
                      </button>
                    )}
                    <button onClick={() => removeReview(r.id)} className="flex items-center justify-center gap-1.5 border border-destructive/60 text-destructive font-bold text-xs uppercase py-2.5 px-3 rounded-sm hover:bg-destructive hover:text-white" data-testid={`delete-${r.id}`}>
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="inventory" className="mt-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between mb-4 border border-border bg-card p-4">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Low-stock alert when stock ≤</span>
                <Input type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="w-20 bg-background border-border rounded-sm h-9 focus-visible:ring-primary focus-visible:ring-1" data-testid="threshold-input" />
                <button onClick={saveThreshold} className="text-xs font-bold uppercase tracking-wide border border-primary text-primary px-3 py-2 rounded-sm hover:bg-primary hover:text-white transition-colors" data-testid="threshold-save">Save</button>
                <button onClick={sendDigest} className="text-xs font-bold uppercase tracking-wide border border-border px-3 py-2 rounded-sm hover:border-primary/60 transition-colors" data-testid="send-digest">Email digest now</button>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={downloadTemplate} className="flex items-center gap-2 border border-border font-bold text-xs uppercase tracking-wide px-4 py-2.5 rounded-sm hover:border-primary/60 transition-colors" data-testid="download-template">
                  <FileArrowDown size={16} weight="bold" /> Template
                </button>
                <label className={`flex items-center gap-2 border border-border font-bold text-xs uppercase tracking-wide px-4 py-2.5 rounded-sm cursor-pointer hover:border-primary/60 transition-colors ${importing ? "opacity-60 pointer-events-none" : ""}`} data-testid="import-csv-label">
                  <UploadSimple size={16} weight="bold" /> {importing ? "Importing…" : "Import CSV"}
                  <input type="file" accept=".csv" onChange={importCsv} className="hidden" data-testid="import-csv-input" />
                </label>
                <button onClick={() => setTireDialog({ open: true, tire: null })} className="flex items-center gap-2 bg-primary text-white font-bold text-xs uppercase tracking-wide px-4 py-2.5 rounded-sm hover:bg-red-500" data-testid="add-tire-btn">
                  <Plus size={16} weight="bold" /> Add tire
                </button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-4">CSV columns: <span className="text-foreground/80">brand, model, width, aspect, diameter, category, price, stock</span> (rating & image optional). Existing brand+model+size rows are updated.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="inventory-list">
              {tires.length === 0 && <Empty label="No tires in catalog" />}
              {tires.map((t) => (
                <div key={t.id} className="border border-border bg-card p-5 flex flex-col" data-testid="inventory-row">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">{t.brand}</span>
                      <h3 className="font-display font-bold text-xl">{t.model}</h3>
                      <div className="text-sm text-foreground/70 mt-1">{t.size} · {t.category}</div>
                    </div>
                    <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-sm ${t.stock > 0 ? "bg-primary/20 text-primary" : "bg-destructive/20 text-red-400"}`}>{t.stock} in stock</span>
                  </div>
                  <div className="font-display font-black text-2xl mt-3">${Number(t.price).toFixed(2)}</div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => setTireDialog({ open: true, tire: t })} className="flex-1 flex items-center justify-center gap-1.5 border border-border font-bold text-xs uppercase py-2.5 rounded-sm hover:border-primary/60" data-testid={`edit-tire-${t.id}`}>
                      <PencilSimple size={16} weight="bold" /> Edit
                    </button>
                    <button onClick={() => deleteTire(t.id)} className="flex items-center justify-center gap-1.5 border border-destructive/60 text-destructive font-bold text-xs uppercase py-2.5 px-3 rounded-sm hover:bg-destructive hover:text-white" data-testid={`delete-tire-${t.id}`}>
                      <Trash size={16} weight="bold" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <TireDialog state={tireDialog} authHeader={authHeader} brands={brands} onClose={() => setTireDialog({ open: false, tire: null })} onSave={saveTire} />

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl uppercase tracking-tight">Change password</DialogTitle>
          </DialogHeader>
          <form onSubmit={changePw} className="space-y-3 mt-2" data-testid="password-form">
            <Input type="password" placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="bg-background border-border rounded-sm h-11 focus-visible:ring-primary focus-visible:ring-1" data-testid="pw-current" />
            <Input type="password" placeholder="New password (min 6 chars)" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="bg-background border-border rounded-sm h-11 focus-visible:ring-primary focus-visible:ring-1" data-testid="pw-new" />
            <Input type="password" placeholder="Confirm new password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="bg-background border-border rounded-sm h-11 focus-visible:ring-primary focus-visible:ring-1" data-testid="pw-confirm" />
            <button type="submit" disabled={pwSaving} className="w-full bg-primary text-white font-bold py-3.5 rounded-sm hover:bg-red-500 transition-colors uppercase tracking-wide disabled:opacity-60" data-testid="pw-submit">
              {pwSaving ? "Updating…" : "Update password"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const StatusBadge = ({ status }) => {
  const map = { pending: "bg-yellow-500/20 text-yellow-400", approved: "bg-primary/20 text-primary", rejected: "bg-destructive/20 text-red-400" };
  return <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-sm ${map[status] || map.pending}`}>{status}</span>;
};

const Empty = ({ label }) => (
  <div className="col-span-full border border-dashed border-border py-16 text-center text-muted-foreground">{label}</div>
);

function TireDialog({ state, authHeader, brands = [], onClose, onSave }) {
  const t = state.tire;
  const blank = { brand: "", model: "", width: "", aspect: "", diameter: "", category: "Performance", price: "", stock: "", rating: "4.5", image: "" };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);

  useEffect(() => {
    if (state.open) {
      setForm(t ? { brand: t.brand, model: t.model, width: t.width, aspect: t.aspect, diameter: t.diameter, category: t.category, price: t.price, stock: t.stock, rating: t.rating, image: t.image } : blank);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.open, state.tire]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadPhoto = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setCropSrc(URL.createObjectURL(f));
  };

  const cancelCrop = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  };

  const handleCropped = async (blob) => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setUploading(true);
    const fd = new FormData();
    fd.append("file", blob, "tire.jpg");
    try {
      const { data } = await api.post("/admin/upload-image", fd, { headers: { ...authHeader } });
      setForm((prev) => ({ ...prev, image: data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.model || !form.width || !form.aspect || !form.diameter || form.price === "" || form.stock === "") {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      await onSave(form, t?.id);
    } catch {
      toast.error("Could not save tire");
    }
    setSaving(false);
  };

  const cls = "bg-background border-border rounded-sm h-11 focus-visible:ring-primary focus-visible:ring-1";

  return (
    <>
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-lg" data-testid="tire-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl uppercase tracking-tight">{t ? "Edit tire" : "Add tire"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="tire-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Input list="brand-options" placeholder="Brand *" value={form.brand} onChange={set("brand")} className={cls} data-testid="tire-brand" />
              <datalist id="brand-options">
                {brands.map((b) => <option key={b.id} value={b.name} />)}
              </datalist>
            </div>
            <Input placeholder="Model *" value={form.model} onChange={set("model")} className={cls} data-testid="tire-model" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Input type="number" placeholder="Width *" value={form.width} onChange={set("width")} className={cls} data-testid="tire-width" />
            <Input type="number" placeholder="Aspect *" value={form.aspect} onChange={set("aspect")} className={cls} data-testid="tire-aspect" />
            <Input type="number" placeholder="Rim *" value={form.diameter} onChange={set("diameter")} className={cls} data-testid="tire-diameter" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className={cls} data-testid="tire-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="number" step="0.1" placeholder="Rating (0-5)" value={form.rating} onChange={set("rating")} className={cls} data-testid="tire-rating" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Price *" type="number" step="0.01" value={form.price} onChange={set("price")} className={cls} data-testid="tire-price" />
            <Input type="number" placeholder="Stock *" value={form.stock} onChange={set("stock")} className={cls} data-testid="tire-stock" />
          </div>

          <div className="flex items-center gap-3">
            <div className="w-16 h-16 border border-border bg-background flex items-center justify-center overflow-hidden shrink-0">
              {form.image ? <img src={form.image} alt="preview" className="w-full h-full object-cover" data-testid="tire-image-preview" /> : <ImageIcon size={22} className="text-muted-foreground" />}
            </div>
            <label className={`flex items-center gap-2 border border-border font-bold text-xs uppercase tracking-wide px-4 py-3 rounded-sm cursor-pointer hover:border-primary/60 transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="tire-upload-label">
              <UploadSimple size={16} weight="bold" /> {uploading ? "Uploading…" : form.image ? "Replace photo" : "Upload photo"}
              <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="tire-image-input" />
            </label>
          </div>
          <button type="submit" disabled={saving} className="w-full bg-primary text-white font-bold py-3.5 rounded-sm hover:bg-red-500 transition-colors uppercase tracking-wide disabled:opacity-60" data-testid="tire-save">
            {saving ? "Saving..." : t ? "Save changes" : "Add tire"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
    <ImageCropper open={!!cropSrc} src={cropSrc} onCancel={cancelCrop} onCropped={handleCropped} />
    </>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="border border-border bg-card p-5">
      {Icon && <Icon size={20} weight="duotone" className="text-primary" />}
      <div className="font-display font-black text-2xl mt-2">{value}</div>
      <div className="text-xs text-muted-foreground tracking-wide mt-1 uppercase">{label}</div>
    </div>
  );
}

function OverviewPanel({ overview, authHeader }) {
  const [range, setRange] = useState({ start: "", end: "" });
  const [filtered, setFiltered] = useState(null);
  const [loadingRange, setLoadingRange] = useState(false);

  if (!overview) return <Empty label="Loading overview…" />;

  const applyRange = async () => {
    if (!range.start || !range.end) { toast.error("Pick both start and end dates"); return; }
    if (range.start > range.end) { toast.error("Start date must be before end date"); return; }
    setLoadingRange(true);
    try {
      const { data } = await api.get("/admin/overview", { params: { start: range.start, end: range.end }, headers: authHeader });
      setFiltered(data.range);
    } catch { toast.error("Could not load range"); }
    setLoadingRange(false);
  };
  const clearRange = () => { setRange({ start: "", end: "" }); setFiltered(null); };

  const series = filtered ? filtered.series : (overview.revenue_series || []);
  const cls = "bg-background border-border rounded-sm h-10 focus-visible:ring-primary focus-visible:ring-1";

  return (
    <div className="space-y-6" data-testid="overview-panel">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Sales logged" value={overview.sales_count} icon={Receipt} />
        <StatCard label="Units sold" value={overview.units_sold} icon={Package} />
        <StatCard label="Low stock items" value={overview.low_stock_count} icon={Warning} />
        <StatCard label="Pending applications" value={overview.pending_apps} icon={Buildings} />
      </div>

      <div className="border border-border bg-card p-5 flex flex-col lg:flex-row lg:items-end gap-3" data-testid="range-filter">
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-muted-foreground">From</label>
          <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className={cls} data-testid="range-start" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-muted-foreground">To</label>
          <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className={cls} data-testid="range-end" />
        </div>
        <button onClick={applyRange} disabled={loadingRange} className="bg-primary text-white font-bold text-xs uppercase tracking-wide px-5 py-2.5 rounded-sm hover:bg-red-500 transition-colors disabled:opacity-60" data-testid="range-apply">{loadingRange ? "Loading…" : "Apply range"}</button>
        {filtered && <button onClick={clearRange} className="text-xs font-bold uppercase tracking-wide border border-border px-4 py-2.5 rounded-sm hover:border-primary/60" data-testid="range-clear">Clear</button>}
        {filtered && (
          <div className="lg:ml-auto flex gap-6 text-sm" data-testid="range-summary">
            <div><span className="text-muted-foreground">Revenue</span> <span className="font-display font-black text-lg text-primary">{money(filtered.revenue)}</span></div>
            <div><span className="text-muted-foreground">Units</span> <span className="font-display font-black text-lg">{filtered.units}</span></div>
            <div><span className="text-muted-foreground">Sales</span> <span className="font-display font-black text-lg">{filtered.sales_count}</span></div>
          </div>
        )}
      </div>

      <div className="border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-6"><ChartLineUp size={20} weight="duotone" className="text-primary" /><h3 className="font-display font-black text-xl uppercase tracking-tight">{filtered ? `Revenue — ${filtered.start} to ${filtered.end}` : "Revenue — last 6 months"}</h3></div>
        <div className="h-64" data-testid="revenue-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
              <XAxis dataKey="month" stroke="#888" tickLine={false} axisLine={{ stroke: "#262626" }} fontSize={12} />
              <YAxis stroke="#888" tickLine={false} axisLine={false} fontSize={12} width={48} />
              <RTooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#121212", border: "1px solid #2a2a2a", borderRadius: 4, color: "#fff" }} formatter={(v) => money(v)} />
              <Bar dataKey="revenue" fill="hsl(0 84% 55%)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-4"><Warning size={20} weight="duotone" className="text-primary" /><h3 className="font-display font-black text-xl uppercase tracking-tight">Low stock (≤ {overview.threshold})</h3></div>
          {(!overview.low_stock || overview.low_stock.length === 0) ? (
            <p className="text-sm text-muted-foreground">All good — nothing below the threshold.</p>
          ) : (
            <ul className="divide-y divide-border" data-testid="low-stock-list">
              {overview.low_stock.map((t, i) => (
                <li key={i} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="font-semibold">{t.brand} {t.model} <span className="text-muted-foreground">· {t.size}</span></span>
                  <span className="text-primary font-bold">{t.stock} left</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-4"><Tag size={20} weight="duotone" className="text-primary" /><h3 className="font-display font-black text-xl uppercase tracking-tight">Top selling brands</h3></div>
          {(!overview.top_brands || overview.top_brands.length === 0) ? (
            <p className="text-sm text-muted-foreground">Log some sales to see your best brands.</p>
          ) : (
            <ul className="space-y-3" data-testid="top-brands-list">
              {overview.top_brands.map((b, i) => {
                const max = overview.top_brands[0].units || 1;
                return (
                  <li key={i}>
                    <div className="flex justify-between text-sm mb-1"><span className="font-semibold">{b.brand}</span><span className="text-muted-foreground">{b.units} units</span></div>
                    <div className="h-2 bg-muted rounded-sm overflow-hidden"><div className="h-full bg-primary" style={{ width: `${(b.units / max) * 100}%` }} /></div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function SalesPanel({ sales, tires, onCreate, onDelete }) {
  const blank = { item: "", tire_id: "", qty: "1", unit_price: "", customer: "", sale_date: new Date().toISOString().slice(0, 10), reduce_stock: true };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const pickTire = (id) => {
    const t = tires.find((x) => x.id === id);
    setForm((f) => ({ ...f, tire_id: id, item: t ? `${t.brand} ${t.model} (${t.size})` : f.item, unit_price: t ? String(t.price) : f.unit_price }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.item || form.unit_price === "" || !form.qty) { toast.error("Item, quantity and unit price are required"); return; }
    setSaving(true);
    try {
      await onCreate({
        item: form.item, tire_id: form.tire_id || null, qty: Number(form.qty),
        unit_price: Number(form.unit_price), customer: form.customer, sale_date: form.sale_date, reduce_stock: form.reduce_stock,
      });
      setForm({ ...blank, sale_date: form.sale_date });
    } catch { toast.error("Could not log sale"); }
    setSaving(false);
  };

  const cls = "bg-background border-border rounded-sm h-11 focus-visible:ring-primary focus-visible:ring-1";
  const total = sales.reduce((a, s) => a + s.total, 0);

  const exportCsv = () => {
    if (sales.length === 0) { toast.error("No sales to export"); return; }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = "date,item,customer,qty,unit_price,total\n";
    const rows = sales.map((s) => [s.sale_date, s.item, s.customer, s.qty, s.unit_price, s.total].map(esc).join(",")).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tireinfluencers-sales-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Sales exported");
  };

  return (
    <div className="space-y-6" data-testid="sales-panel">
      <form onSubmit={submit} className="border border-border bg-card p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sale-form">
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-muted-foreground">From catalog (optional)</label>
          <Select value={form.tire_id} onValueChange={pickTire}>
            <SelectTrigger className={cls} data-testid="sale-tire"><SelectValue placeholder="Select a tire" /></SelectTrigger>
            <SelectContent className="bg-popover border-border max-h-64">
              {tires.map((t) => <SelectItem key={t.id} value={t.id}>{t.brand} {t.model} · {t.size}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-muted-foreground">Item *</label>
          <Input value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} className={cls} data-testid="sale-item" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-muted-foreground">Customer</label>
          <Input value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} className={cls} data-testid="sale-customer" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-muted-foreground">Quantity *</label>
          <Input type="number" min="1" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className={cls} data-testid="sale-qty" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-muted-foreground">Unit price *</label>
          <Input type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className={cls} data-testid="sale-price" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-muted-foreground">Date</label>
          <Input type="date" value={form.sale_date} onChange={(e) => setForm({ ...form, sale_date: e.target.value })} className={cls} data-testid="sale-date" />
        </div>
        <label className="flex items-center gap-2 text-sm text-foreground/80 sm:col-span-2">
          <input type="checkbox" checked={form.reduce_stock} onChange={(e) => setForm({ ...form, reduce_stock: e.target.checked })} data-testid="sale-reduce-stock" />
          Reduce catalog stock for this tire
        </label>
        <button type="submit" disabled={saving} className="bg-primary text-white font-bold py-3 rounded-sm hover:bg-red-500 transition-colors uppercase tracking-wide disabled:opacity-60 self-end" data-testid="sale-submit">
          {saving ? "Logging…" : "Log sale"}
        </button>
      </form>

      <div className="flex items-center justify-between">
        <h3 className="font-display font-black text-xl uppercase tracking-tight">Recent sales</h3>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">Total: <span className="text-primary font-bold">{money(total)}</span></span>
          <button onClick={exportCsv} className="flex items-center gap-2 border border-border font-bold text-xs uppercase tracking-wide px-4 py-2.5 rounded-sm hover:border-primary/60 transition-colors" data-testid="export-sales">
            <FileArrowDown size={16} weight="bold" /> Export CSV
          </button>
        </div>
      </div>
      <div className="border border-border" data-testid="sales-list">
        {sales.length === 0 && <Empty label="No sales logged yet" />}
        {sales.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-4 p-4 border-b border-border last:border-0 bg-card" data-testid="sale-row">
            <div className="flex-1">
              <div className="font-semibold">{s.item}</div>
              <div className="text-xs text-muted-foreground">{s.sale_date} · {s.qty} × {money(s.unit_price)}{s.customer ? ` · ${s.customer}` : ""}</div>
            </div>
            <div className="font-display font-black text-lg">{money(s.total)}</div>
            <button onClick={() => onDelete(s.id)} className="p-2 border border-destructive/60 text-destructive rounded-sm hover:bg-destructive hover:text-white transition-colors" data-testid={`sale-delete-${s.id}`}><Trash size={16} weight="bold" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function BrandLogoUpload({ authHeader, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/upload-image", fd, { headers: { ...authHeader } });
      onChange(data.url);
      toast.success("Logo uploaded");
    } catch { toast.error("Upload failed"); }
    setBusy(false);
  };
  return (
    <label className={`flex items-center justify-center w-11 h-11 border border-border rounded-sm cursor-pointer overflow-hidden shrink-0 hover:border-primary/60 ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid={testid}>
      {value ? <img src={value} alt="logo" className="w-full h-full object-cover" /> : <UploadSimple size={16} className="text-muted-foreground" />}
      <input type="file" accept="image/*" onChange={upload} className="hidden" />
    </label>
  );
}

function BrandsPanel({ brands, authHeader, onAdd, onRename, onDelete }) {
  const [newName, setNewName] = useState("");
  const [newLogo, setNewLogo] = useState(null);
  const [edits, setEdits] = useState({});

  const cls = "bg-background border-border rounded-sm h-11 focus-visible:ring-primary focus-visible:ring-1";
  const nameOf = (b) => (edits[b.id]?.name !== undefined ? edits[b.id].name : b.name);
  const logoOf = (b) => (edits[b.id]?.logo !== undefined ? edits[b.id].logo : b.logo);
  const setEdit = (id, patch) => setEdits((e) => ({ ...e, [id]: { ...e[id], ...patch } }));

  const add = async () => {
    if (!newName.trim()) { toast.error("Enter a brand name"); return; }
    await onAdd(newName.trim(), newLogo);
    setNewName(""); setNewLogo(null);
  };

  return (
    <div className="space-y-6" data-testid="brands-panel">
      <div className="border border-border bg-card p-6 flex flex-col sm:flex-row sm:items-end gap-3">
        <BrandLogoUpload authHeader={authHeader} value={newLogo} onChange={setNewLogo} testid="new-brand-logo" />
        <div className="flex-1 flex flex-col gap-1.5">
          <label className="text-[10px] font-bold tracking-[0.15em] uppercase text-muted-foreground">New brand / tire company</label>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Nokian" className={cls} data-testid="new-brand-name" />
        </div>
        <button onClick={add} className="flex items-center justify-center gap-2 bg-primary text-white font-bold text-xs uppercase tracking-wide px-5 py-3 rounded-sm hover:bg-red-500 transition-colors" data-testid="add-brand-btn">
          <Plus size={16} weight="bold" /> Add brand
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="brands-list">
        {brands.length === 0 && <Empty label="No brands yet" />}
        {brands.map((b) => (
          <div key={b.id} className="border border-border bg-card p-4 flex items-center gap-3" data-testid="brand-row">
            <BrandLogoUpload authHeader={authHeader} value={logoOf(b)} onChange={(url) => setEdit(b.id, { logo: url })} testid={`brand-logo-${b.id}`} />
            <div className="flex-1 min-w-0">
              <Input value={nameOf(b)} onChange={(e) => setEdit(b.id, { name: e.target.value })} className="bg-background border-border rounded-sm h-9 focus-visible:ring-primary focus-visible:ring-1" data-testid={`brand-name-${b.id}`} />
              <div className="text-[11px] text-muted-foreground mt-1">{b.tire_count} tire{b.tire_count === 1 ? "" : "s"}</div>
            </div>
            <button onClick={() => onRename(b.id, nameOf(b), logoOf(b))} className="text-[10px] font-bold uppercase border border-primary text-primary px-2.5 py-2 rounded-sm hover:bg-primary hover:text-white transition-colors" data-testid={`brand-save-${b.id}`}>Save</button>
            <button onClick={() => onDelete(b.id)} className="p-2 border border-destructive/60 text-destructive rounded-sm hover:bg-destructive hover:text-white transition-colors" data-testid={`brand-delete-${b.id}`}><Trash size={16} weight="bold" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
